import { useEffect } from "react";
import type { AdminPayout } from "@/mocks/admin";

type Props = {
  payout: AdminPayout | null;
  onClose: () => void;
  onPay: () => void;
  onRetry: () => void;
};

const statusPill: Record<AdminPayout["status"], string> = {
  paid: "bg-primary-100 text-primary-700",
  pending: "bg-background-200 text-foreground-700",
  processing: "bg-accent-100 text-accent-700",
  failed: "bg-accent-200 text-accent-800",
};

const statusLabel: Record<AdminPayout["status"], string> = {
  paid: "Paid",
  pending: "Pending",
  processing: "Processing",
  failed: "Failed",
};

const methodIcon: Record<AdminPayout["method"], string> = {
  ACH: "ri-bank-line",
  Instant: "ri-flashlight-line",
  Check: "ri-mail-line",
};

export default function PayoutDrawer({ payout, onClose, onPay, onRetry }: Props) {
  useEffect(() => {
    if (!payout) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [payout, onClose]);

  if (!payout) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <aside className="absolute right-0 top-0 h-full w-full md:w-[440px] bg-background-50 overflow-y-auto animate-slide-in">
        <div className="flex items-center justify-between px-5 pt-5">
          <p className="text-xs uppercase tracking-widest text-foreground-500">
            Payout · {payout.id}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-5 pb-6 pt-3">
          {/* Identity */}
          <div className="flex items-center gap-4">
            <img
              src={payout.cleanerAvatar}
              alt={payout.cleanerName}
              className="w-16 h-16 rounded-full object-cover object-top"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-xl text-foreground-950 truncate">
                  {payout.cleanerName}
                </h3>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusPill[payout.status]}`}
                >
                  {statusLabel[payout.status]}
                </span>
              </div>
              <p className="text-xs text-foreground-600 mt-0.5">
                {payout.cleanerRole}
              </p>
              <p className="text-xs text-foreground-600">{payout.periodLabel}</p>
            </div>
          </div>

          {/* Amount card */}
          <div className="bg-foreground-950 text-background-50 rounded-xl p-5 mt-5">
            <p className="text-[10px] uppercase tracking-widest text-background-50/60">
              Net payout
            </p>
            <p className="font-heading text-3xl mt-1">
              ${payout.net.toLocaleString()}
            </p>
            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-background-50/10">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-background-50/60">
                  Gross
                </p>
                <p className="text-sm font-semibold mt-0.5">
                  ${payout.gross.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-background-50/60">
                  Platform fee
                </p>
                <p className="text-sm font-semibold mt-0.5">
                  −${payout.fees.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="mt-5 space-y-2">
            <div className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-3">
              <span className="text-xs text-foreground-600">Jobs completed</span>
              <span className="text-sm font-semibold text-foreground-950">
                {payout.jobs}
              </span>
            </div>
            <div className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-3">
              <span className="text-xs text-foreground-600">Payout method</span>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-950">
                <i className={`${methodIcon[payout.method]} text-foreground-700`}></i>
                {payout.method}
              </span>
            </div>
            <div className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-3">
              <span className="text-xs text-foreground-600">Destination</span>
              <span className="text-sm font-semibold text-foreground-950">
                {payout.account}
              </span>
            </div>
            <div className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-3">
              <span className="text-xs text-foreground-600">Scheduled for</span>
              <span className="text-sm font-semibold text-foreground-950">
                {payout.scheduledFor}
              </span>
            </div>
            {payout.paidAt && (
              <div className="flex items-center justify-between bg-background-100 rounded-lg px-4 py-3">
                <span className="text-xs text-foreground-600">Paid at</span>
                <span className="text-sm font-semibold text-foreground-950">
                  {payout.paidAt}
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center gap-2">
            {payout.status === "pending" && (
              <button
                onClick={onPay}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line"></i>
                Pay now
              </button>
            )}
            {payout.status === "failed" && (
              <button
                onClick={onRetry}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-accent-600 hover:bg-accent-700 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Retry payout
              </button>
            )}
            <button className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-900 text-sm font-medium whitespace-nowrap cursor-pointer">
              <i className="ri-download-2-line"></i>
              Statement
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}